import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";
import { formatDate, getItemById, typeLabels } from "@/utils/content";

const slideIds = [
  "news-premiere-announcement",
  "plot-chapter-06",
  "character-lin-jingshu",
  "plot-chapter-04",
];

export default function HeroCarousel() {
  const slides = slideIds
    .map((id) => getItemById(id))
    .filter((item): item is ContentItem => Boolean(item));
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-foreground-950">
      {slides.map((slide, idx) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.cover}
            alt={slide.title}
            title={`${slide.title} - 年轻的母亲3`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto h-full flex flex-col justify-center">
          <div className="max-w-2xl animate-fade-up" key={slides[active].id}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500 text-[13px] font-medium text-accent-950 mb-5">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-fire-line"></i></span>
              {typeLabels[slides[active].type]} · 焦点推荐
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl leading-tight text-white">
              {slides[active].title}
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-white/85 leading-relaxed line-clamp-3 max-w-xl">
              {slides[active].summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[active].id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看详情
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
              <Link
                to={`/category/${slides[active].type}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white/10 border border-white/30 text-white text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                更多{typeLabels[slides[active].type]}
              </Link>
            </div>
            <p className="mt-6 text-xs text-white/60">
              <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1"><i className="ri-calendar-line"></i></span>
              更新于 {formatDate(slides[active].date)}
            </p>
          </div>
        </div>
      </div>

      <div className="absolute z-20 top-1/2 -translate-y-1/2 right-4 md:right-8 flex flex-col items-center gap-2.5">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15 border border-white/30 text-white flex items-center justify-center hover:bg-white/25 transition-colors cursor-pointer backdrop-blur-sm"
        >
          <i className="ri-arrow-up-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15 border border-white/30 text-white flex items-center justify-center hover:bg-white/25 transition-colors cursor-pointer backdrop-blur-sm"
        >
          <i className="ri-arrow-down-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-20 md:bottom-24 left-4 md:left-8 flex items-center gap-2">
        {slides.map((slide, idx) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${idx + 1} 张`}
            onClick={() => setActive(idx)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === active ? "w-8 bg-accent-500" : "w-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}